import React, { memo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { addDays, subMonths, isAfter } from 'date-fns';
import { IconButton } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {
  getOptions,
  HighestEducation,
  windstormMitigationDiscountStates,
  stormShuttersStates,
  hideHighestEducationStates,
  windstormMitigationDiscountYearBuilt,
  stormShuttersInAdvancedWindstormSectionStates,
  fortifiedHomeStates,
  lookupsJson,
  policyType
} from '@ourbranch/lookups';
import { enhancedRoofWindstormStates } from '@ourbranch/roof-helpers';
import Button from '@material-ui/core/Button';
import config from 'aws-exports';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useToast } from 'core/components/toast';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { useStore } from 'core/store/store.mobx';
import { DwellingAddress } from 'common/components/dwelling-address';
import { useCurrentState } from 'common/hooks/useCurrentState';
import CollapsibleCard from 'core/components/collapsible-card';
import { FieldWithTooltip } from 'core/components/field-with-tooltip';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import Section from 'core/components/section';
import FormList from 'core/components/form-list';
import withDatePicker from 'core/components/with-date-picker';
import { NotificationCard } from 'core/components/notification-card';
import { Tooltip } from 'core/components/tooltip';
import useSession from 'core/hooks/use-session';
import NotificationIconGreen from 'core/assets/svg/ic-info-dark.svg';
import ResetIcon from 'core/assets/svg/ic-reset.svg';
import { generateZillowLink } from 'core/helpers/property-view-links';
import { setPrimaryMortgageValues } from 'offer/helpers/mortgage-helpers';
import AdvancedWindstormDetails from './advanced-windstorm-details/advanced-windstorm-details';
import { StormShutters } from './storm-shutters';
import { AddMortgage, MortgageItem } from '../../mortgage-details';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import { brandConstants } from 'brand-config';

import useStyles from './detail.styles';

const id = 'home';
const ROOF_SHAPE_GUIDE_ID = 'e6d2dadb-66c8-46d1-9aee-b51df3ce2e69';

const Detail = memo(function Detail({ disabled, fromPolicy }) {
  const { offer: store } = useStore();
  const classes = useStyles();
  const { canEditUneditablePropertyDetailsFields, isAgency, isInternalAgent } = useSession();
  const isAgentSold = store.offer?.quote?.isAgentSold;
  const { values, setFieldValue, setFieldTouched, initialValues } = useFormikContext();
  const { home } = values;
  const state = useCurrentState(values, { policyType: policyType.Home });
  const showStormShutters = stormShuttersStates.includes(state);
  const showEnhancedRoofWindstorm = enhancedRoofWindstormStates[state];
  const showWindstormMitigationLevel =
    windstormMitigationDiscountStates[state] &&
    (!home.yearBuilt || home.yearBuilt < windstormMitigationDiscountYearBuilt);
  const showAdvancedWindstormDetails = showEnhancedRoofWindstorm || showWindstormMitigationLevel || showStormShutters;
  const showStormShuttersInAdvancedSection =
    stormShuttersStates.includes(state) && stormShuttersInAdvancedWindstormSectionStates.includes(state);
  const showStormShuttersInRoofSection = stormShuttersStates.includes(state) && !showStormShuttersInAdvancedSection;
  const mortgageDetails = home.mortgageDetails;
  const estimatedCost = home.replacementCostEstimate?.total?.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
  const toast = useToast();
  const { inOfferEditRoute, inPolicyRoute } = useRouteHelper();

  const sixMonthsAgo = subMonths(new Date(), 6);
  const homePurchasedWithinSixMonths = isAfter(new Date(values.home.purchaseDate), sixMonthsAgo);

  const location = home.homeLocation;
  const address = `${location.address} ${location.address2 ?? ''} ${location.city} ${location.state} ${location.zip}`;

  useEffect(() => {
    if (mortgageDetails.length > 0) {
      if (!mortgageDetails.find((item) => item.primary)) {
        const mortgage = mortgageDetails[0];
        setFieldValue(`${id}.mortgageDetails.0`, { ...mortgage, primary: true });
        setPrimaryMortgageValues(setFieldValue, mortgage, values.primaryMortgageDetail);
      }
    }
  }, [mortgageDetails, setFieldValue]);

  const toastRCEWarning = useCallback(() => {
    if (
      home.replacementCostEstimate?.total != null &&
      home.replacementCostEstimate.total !== values.homeCoverage.coverageA
    ) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  }, [toast, values.homeCoverage.coverageA, home.replacementCostEstimate?.total]);

  const showChangedSqFtNotification = inOfferEditRoute && home.originalSqFt && home.sqFt !== home.originalSqFt;

  //Roof year notification only shows if the offer roofYear has been updated after pulling Cape data and is newer than the year provided by Cape.

  const { offer, skippedDataPulls } = store;
  const { roofReplacementYearMax } = offer?.quote?.home || {};
  const isCapePulled = !skippedDataPulls?.includes('cape-analytics');
  const showChangedRoofYearNotification =
    inOfferEditRoute &&
    isCapePulled &&
    roofReplacementYearMax !== null &&
    home.roofYear !== roofReplacementYearMax && // home !=== offer.quote.home when updating offer, clears roofReplacementYearMax on update if use home.roofReplacementYearMax
    home.roofYear > roofReplacementYearMax;

  const roofYearNotificationText = !isAgentSold
    ? {
        header: 'Verify roof year before binding',
        body: `Please ensure the information entered is correct by verifying proof prior to bind. 
    Acceptable proof includes a paid invoice or signed contract with the completion date. 
    If the roof was replaced before ownership, roof permits or seller’s disclosure are also accepted. 
    If proof is unavailable, reset the roof year to on or before ${roofReplacementYearMax}. `
      }
    : {
        header: 'Due to the roof year entered, we will require proof post-purchase',
        body: `The roof year entered is newer than what was pulled from ${brandConstants.SHORT_NAME}’s satellite imagery data source (${roofReplacementYearMax}) and will require acceptable proof post-purchase.`
      };

  if (!home) {
    return null;
  }
  return (
    <Section
      // Scroll anchor for the validation-errors modal "Home details" go-to (see
      // validation-errors-modal.constants.js SECTION_NAV_TARGETS). `id` is 'home'.
      id={id}
      title="Home details"
      type="SubSection"
      right={
        <Grid container className={classes.links}>
          <Grid className={classes.linkAndIcon}>
            <a
              rel="noreferrer noopener"
              target="_blank"
              href={`https://maps.googleapis.com/maps/api/streetview?size=1200x800&location=${home.latitude},${home.longitude}&fov=80&key=${config.googleGeocoderKey}`}
              className={classes.streetMapLink}
            >
              Street View
              <img src={externalLinkIcon} alt="link to google maps" />
            </a>
          </Grid>
          <Grid className={classes.linkAndIcon}>
            <a
              rel="noreferrer noopener"
              target="_blank"
              href={`https://earth.google.com/web/search/=${address}`}
              className={classes.streetMapLink}
            >
              Google Earth
              <img src={externalLinkIcon} alt="link to google earth" />
            </a>
          </Grid>
          <Grid className={classes.linkAndIcon}>
            <a
              rel="noreferrer noopener"
              target="_blank"
              href={generateZillowLink(location)}
              className={classes.streetMapLink}
            >
              Zillow
              <img src={externalLinkIcon} alt="link to zillow" />
            </a>
          </Grid>
          {home.capeConditions?.roofAgeImage && (
            <Grid className={classes.linkAndIcon}>
              <a
                rel="noreferrer noopener"
                target="_blank"
                href={home.capeConditions.roofAgeImage}
                className={classes.streetMapLink}
              >
                Roof Age
                <img src={externalLinkIcon} alt="link to roof age" />
              </a>
            </Grid>
          )}
          {home.capeConditions?.roofConditionImage && (
            <Grid className={classes.linkAndIcon}>
              <a
                rel="noreferrer noopener"
                target="_blank"
                href={home.capeConditions.roofConditionImage}
                className={classes.streetMapLink}
              >
                Roof Condition
                <img src={externalLinkIcon} alt="link to roof condition" />
              </a>
            </Grid>
          )}
        </Grid>
      }
    >
      <CollapsibleCard
        initialOpen
        content={
          <DwellingAddress
            dwelling="Home"
            fieldName="home.homeLocation"
            location={location}
            includeApt={home.homeLocation.address2?.length > 0}
            disabled={disabled}
          />
        }
      >
        <Grid container justifyContent="space-around" className={classes.containerDark}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <Label type="formSubTitleLight">Residents</Label>
            </Grid>
            <FormField
              name={`${id}.ownershipStatus`}
              type="select"
              label="Ownership"
              mode="light"
              options={getOptions('ownershipStatus')}
              xs={6}
            />

            <FormField
              name={`${id}.numOccupants`}
              type="numeric"
              label="Residents"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={6}
            />
            {!hideHighestEducationStates.includes(state) && (
              <FormField
                name="highestEducation"
                type="value"
                label="Highest Education of App/Co-App"
                value={HighestEducation[values.highestEducation] || 'N/A'}
                xs={6}
              />
            )}
            <FormField
              name="oldestResident"
              type="value"
              label="Age of Oldest App/Co-App"
              value={values.oldestResident || 'N/A'}
              xs={hideHighestEducationStates.includes(state) ? 12 : 6}
            />

            <Grid item xs={12}>
              <Label type="formSubTitleLight">Purchase Information</Label>
            </Grid>
            <FormField
              name={`${id}.purchaseDate`}
              type="date"
              label="House Purchased"
              mode="light"
              xs={6}
              disableFuture={false}
              maxDate={addDays(new Date(), 60)}
            />
            {homePurchasedWithinSixMonths && !['MI', 'MD', 'PA'].includes(state) && (
              <Grid item xs={6}>
                <FieldWithTooltip
                  name={`${id}.purchaseAmount`}
                  type="numeric"
                  label="Purchase Price"
                  mode="light"
                  xs={6}
                  tooltip={tooltipHoverTexts.purchaseAmount}
                  fast={false}
                  format={{ thousandSeparator: true, prefix: '$' }}
                />
              </Grid>
            )}
            {(!homePurchasedWithinSixMonths || ['MI', 'MD', 'PA'].includes(state)) && (
              <Grid item xs={6}>
                <FieldWithTooltip
                  name={`${id}.purchaseAmount`}
                  type="value"
                  label="Purchase Price"
                  mode="light"
                  xs={6}
                  tooltip={tooltipHoverTexts.purchaseAmount}
                  value={'N/A'}
                  labelNextToTooltip={!home.purchaseAmount}
                  fast={false}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Label type="formSubTitleLight">Property</Label>
            </Grid>
            {canEditUneditablePropertyDetailsFields || inOfferEditRoute ? (
              <>
                <FormField
                  name={`${id}.yearBuilt`}
                  type="numeric"
                  label="Year built in"
                  mode="light"
                  format={{
                    allowNegative: false
                  }}
                  xs={4}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.yearBuilt) {
                      toastRCEWarning();
                    }
                  }}
                />
              </>
            ) : (
              <>
                <Grid item xs={4}>
                  <FieldWithTooltip
                    name={`${id}.yearBuilt`}
                    label="Year built in"
                    mode="light"
                    lockedValue={{
                      text: inOfferEditRoute
                        ? tooltipHoverTexts.uneditableFieldsTooltipText
                        : tooltipHoverTexts.uneditableFieldsPolicy
                    }}
                    type="value"
                  />
                </Grid>
              </>
            )}

            {(canEditUneditablePropertyDetailsFields || inOfferEditRoute) && (
              <FormField
                name={`${id}.sqFt`}
                type="numeric"
                label="Above-Ground SqFt"
                mode="light"
                format={{
                  allowNegative: false
                }}
                xs={4}
                onChange={(val) => {
                  if (inOfferEditRoute && val !== initialValues.home.sqFt) {
                    toastRCEWarning();
                  }
                }}
              />
            )}

            {!canEditUneditablePropertyDetailsFields && inPolicyRoute && (
              <Grid item xs={4}>
                <FieldWithTooltip
                  name={`${id}.sqFt`}
                  type="value"
                  label="Above-Ground SqFt"
                  mode="light"
                  lockedValue={{
                    text: tooltipHoverTexts.uneditableFieldsPolicy
                  }}
                />
              </Grid>
            )}

            <FormField
              name={`${id}.basementSqFt`}
              type="numeric"
              label="Basement SqFt"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.basementSqFt) {
                  toastRCEWarning();
                }
              }}
            />
            {!!showChangedSqFtNotification && (
              <NotificationCard
                type="light"
                className={classes.cardStyling}
                iconByHeader
                icon={NotificationIconGreen}
                iconSize="icon16Bold"
                header={`The above-ground square footage has been modified from its original verified value of ${home.originalSqFt} square
                    feet, which was obtained from a third-party data source.`}
              >
                <Grid container className={classes.notificationText}>
                  Please ensure the information entered is accurate. If found incorrect, it will be updated, which may
                  lead to a premium increase.
                  <Grid container className={classes.buttonContainer}>
                    <Button
                      color={'secondary'}
                      variant={'text'}
                      className={classes.button}
                      onClick={() => {
                        setFieldValue(`${id}.sqFt`, home.originalSqFt);
                        setFieldTouched(`${id}.sqFt`, true);
                      }}
                    >
                      <img src={ResetIcon} className={classes.iconStyle} />
                      Reset Above-ground sq. ft.
                    </Button>
                  </Grid>
                </Grid>
              </NotificationCard>
            )}
            <FormField
              name={`${id}.typeOfHome`}
              type="select"
              label="Home Type"
              mode="light"
              options={getOptions('typeOfHome')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.typeOfHome) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numFamiliesInDwelling`}
              type="numeric"
              label="Families in Dwelling"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numFamiliesInDwelling) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.homeQuality`}
              type="select"
              label="Home Quality"
              mode="light"
              options={getOptions('homeQuality')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.homeQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numStories`}
              type="numeric"
              label="Number of Stories"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={3}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numStories) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numHalfBathrooms`}
              type="numeric"
              label="Number of Half Bathrooms"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={3}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numHalfBathrooms) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numFullBathrooms`}
              type="numeric"
              label="Number of Full Bathrooms"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={3}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numFullBathrooms) {
                  toastRCEWarning();
                }
              }}
            />
            {canEditUneditablePropertyDetailsFields || inOfferEditRoute ? (
              <FormField
                name={`${id}.constructionType`}
                type="select"
                label="Construction Type"
                mode="light"
                options={getOptions('constructionType')}
                xs={3}
                onChange={(val) => {
                  if (inOfferEditRoute && val !== initialValues.home.constructionType) {
                    toastRCEWarning();
                  }
                }}
              />
            ) : (
              <Grid item xs={3}>
                <FieldWithTooltip
                  name={`${id}.constructionType`}
                  label="Construction Type"
                  mode="light"
                  lockedValue={{
                    text: inOfferEditRoute
                      ? tooltipHoverTexts.uneditableFieldsTooltipText
                      : tooltipHoverTexts.uneditableFieldsPolicy
                  }}
                  type="value"
                  formatter={(value) => lookupsJson.constructionType.find(({ id }) => id === value).value}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.constructionType) {
                      toastRCEWarning();
                    }
                  }}
                />
              </Grid>
            )}
            <FormField
              name={`${id}.numFireplaces`}
              type="numeric"
              label="Number of Fireplaces"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numFireplaces) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numWoodBurningStoves`}
              type="numeric"
              label="Number of Wood Stoves"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numWoodBurningStoves) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.basementType`}
              type="select"
              label="Basement/Foundation Type"
              mode="light"
              options={getOptions('basementType')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.basementType) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.exteriorWallType`}
              type="select"
              label="Exterior Wall finish"
              mode="light"
              options={getOptions('exteriorWallType')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.exteriorWallType) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.garageCarCapacity`}
              type="numeric"
              label="Garage's car space"
              mode="light"
              format={{
                allowNegative: false
              }}
              disabled={home.garageType === 'X'}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.garageCarCapacity) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.garageType`}
              type="select"
              label="Garage Type"
              mode="light"
              options={getOptions('garageType')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.garageType) {
                  toastRCEWarning();
                }
              }}
            />
            {isAgency && fromPolicy ? (
              <Grid item xs={3}>
                <FieldWithTooltip
                  name={`${id}.roofYear`}
                  type="value"
                  label="Roof Year"
                  mode="light"
                  tooltip={tooltipHoverTexts.roofYearDisabled}
                  labelNextToTooltip
                />
              </Grid>
            ) : (
              <Grid item xs={4}>
                <FieldWithTooltip
                  name={`${id}.roofYear`}
                  type="numeric"
                  label="Roof Year"
                  mode="light"
                  tooltip={tooltipHoverTexts.roofYear}
                  format={{
                    allowNegative: false
                  }}
                  // using touched to know if we are focused on this field in the other-coverages for coverageRSExtended
                  onFocus={() => setFieldTouched(`${id}.roofYear`, true, true)}
                  onBlur={() => setFieldTouched(`${id}.roofYear`, false, true)}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.roofYear) {
                      toastRCEWarning();
                    }
                  }}
                />
              </Grid>
            )}

            <FormField
              name={`${id}.roofType`}
              type="select"
              label="Roof Type"
              mode="light"
              options={getOptions('roofType', state)}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.roofType) {
                  toastRCEWarning();
                }
              }}
            />
            {canEditUneditablePropertyDetailsFields || inOfferEditRoute ? (
              <Grid item xs={4}>
                <FieldWithTooltip
                  name={`${id}.roofShape`}
                  type="select"
                  label="Roof Shape"
                  mode="light"
                  options={getOptions('roofShape')}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.roofShape) {
                      toastRCEWarning();
                    }
                  }}
                  tooltip={
                    inOfferEditRoute && {
                      onClick: () => {
                        window.Appcues?.show(ROOF_SHAPE_GUIDE_ID);
                      },
                      label: 'View Guide',
                      onHoverText: 'Click to view guide',
                      clickIcon: arrowIcon
                    }
                  }
                />
              </Grid>
            ) : (
              <Grid item xs={3}>
                <FieldWithTooltip
                  name={`${id}.roofShape`}
                  label="Roof Shape"
                  mode="light"
                  options={getOptions('roofShape')}
                  lockedValue={{
                    text: inOfferEditRoute
                      ? tooltipHoverTexts.uneditableFieldsTooltipText
                      : tooltipHoverTexts.uneditableFieldsPolicy
                  }}
                  type="value"
                  formatter={(value) => lookupsJson.roofShape.find(({ id }) => id === value).value}
                  tooltip={
                    inOfferEditRoute && {
                      onClick: () => {
                        window.Appcues?.show(ROOF_SHAPE_GUIDE_ID);
                      },
                      label: 'View Guide',
                      onHoverText: 'Click to view guide',
                      clickIcon: arrowIcon
                    }
                  }
                />
              </Grid>
            )}

            {!!showChangedRoofYearNotification && (
              <NotificationCard
                type="light"
                className={classes.cardStyling}
                iconByHeader
                icon={NotificationIconGreen}
                iconSize="icon16Bold"
                header={roofYearNotificationText.header}
              >
                <Grid container className={classes.notificationText}>
                  {roofYearNotificationText.body}
                  <Grid container className={classes.buttonContainer}>
                    {isAgentSold && (
                      <IconButton
                        color="secondary"
                        variant="text"
                        className={classes.button}
                        onClick={() => {
                          window?.Appcues?.show('4496af53-16c3-4c10-9027-2f585bb65e6a');
                        }}
                      >
                        <img src={arrowIcon} className={classes.iconStyle} alt="Learn More" />
                        <div className={classes.button}>Learn More</div>
                      </IconButton>
                    )}

                    <IconButton
                      color={'secondary'}
                      variant={'text'}
                      className={classes.button}
                      onClick={() => {
                        setFieldValue(`${id}.roofYear`, home.roofReplacementYearMax);
                        setFieldTouched(`${id}.roofYear`, true);
                      }}
                    >
                      <img src={ResetIcon} className={classes.iconStyle} alt="Reset roof year" />
                      Reset roof year
                    </IconButton>
                  </Grid>
                </Grid>
              </NotificationCard>
            )}

            {fortifiedHomeStates.includes(state) && (
              <Grid item xs={12}>
                <Grid item xs={6}>
                  <FieldWithTooltip
                    name={`${id}.fortifiedHome`}
                    id={`${id}.fortifiedHome`}
                    type="select"
                    label="Fortified Home Discount"
                    options={lookupsJson.fortifiedHome}
                    mode="light"
                    tooltip={tooltipHoverTexts.fortifiedHome(inOfferEditRoute, inPolicyRoute)}
                  />
                </Grid>
              </Grid>
            )}
            {showStormShuttersInRoofSection && <StormShutters />}
            {showStormShuttersInRoofSection && showAdvancedWindstormDetails && (
              <AdvancedWindstormDetails fromPolicy={fromPolicy} />
            )}
            <FormField
              name={`${id}.fireHydrantWithin1000ft`}
              id={`${id}.fireHydrantWithin1000ft`}
              type="checkbox"
              label="Fire hydrant within 1,000 feet?"
              mode="light"
              xs={12}
            />
            <FormField name={`${id}.fpc`} type="value" label="Fire Protection Class" value={home.fpc || 'N/A'} xs={4} />
            <FormField
              name={`${id}.wildfireHazardScore`}
              type="value"
              label="Wildfire Hazard Score"
              value={home.wildfireHazardScore || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.wildfireGallagherScore`}
              type="value"
              label="Wildfire Gallagher Score"
              value={home.wildfireGallagherScore || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.dtoName`}
              type="value"
              label="Distance to Ocean"
              value={home.dtoName || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.dtbowLabel`}
              type="value"
              label="Nearest BOW"
              value={home.dtbowLabel || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.dtbowName`}
              type="value"
              label="Distance to BOW"
              value={home.dtbowName || 'N/A'}
              xs={4}
            />
            {!showStormShuttersInRoofSection && showAdvancedWindstormDetails && (
              <AdvancedWindstormDetails fromPolicy={fromPolicy} />
            )}

            <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
              <Grid container justifyContent="space-between">
                <Grid container xs={8} className={classes.rceValues} alignContent="center">
                  <Label>Our estimated cost to replace this home</Label>
                  <Tooltip
                    label="More info"
                    text={
                      isInternalAgent
                        ? `${tooltipHoverTexts.rceText} The 360 Value Valuation ID for this estimate is ${home.replacementCostEstimate?.valuationId || 'N/A'}.`
                        : tooltipHoverTexts.rceText
                    }
                  />
                </Grid>
                <Grid container xs={4} justifyContent="flex-end" className={classes.rceValues}>
                  <Label>RCE</Label>
                  <Label type="infoValueBold">{estimatedCost || 'N/A'}</Label>
                </Grid>
              </Grid>
            </NotificationCard>
          </Grid>
        </Grid>
        <Grid container className={classes.mortgagesContainer}>
          <FormList
            classes={{ container: classes.container }}
            disabled={disabled}
            id={`${id}.mortgageDetails`}
            item={MortgageItem}
            type="InnerSection"
            addForm={AddMortgage}
            title="Mortgage lender information"
            singular="Mortgage"
            plural="Mortgages"
          />
        </Grid>
      </CollapsibleCard>
    </Section>
  );
});

Detail.propTypes = {
  disabled: PropTypes.bool,
  fromPolicy: PropTypes.bool
};

Detail.defaultProps = {
  disabled: false,
  fromPolicy: false
};

export default withDatePicker(Detail);
